import React, { useContext, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { Button, Col, Form, FormGroup, Modal, Row } from 'react-bootstrap';
import Notiflix from 'notiflix';
import PropTypes from 'prop-types';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { AuthContext } from 'context/Context';
import { useCreateRuleMutation } from 'modules/rules/mutations/useCreateRuleMutation';
import { useEditRuleMutation } from 'modules/rules/mutations/useEditRuleMutation';
import { useGetRuleQuery } from 'modules/rules/queries/useGetRuleQuery';
import { useDeleteRulesMutation } from 'modules/rules/mutations/useDeleteRulesMutation';
import { Loader } from 'components/common/Loader';

const DEFAULT_FORM_VALUES = { name: '', ruleISsmallplaintextbox: '', type: { value: 'text', label: 'text' } };

const ManageRulesModal = ({ isVisible, onClose, ruleId, resetRuleId }) => {
  const queryClient = useQueryClient();
  const existedRules = queryClient.getQueryData(['ALL_RULES']);
  const { appSettings, user } = useContext(AuthContext);
  const fieldTypes = appSettings?.dropdowns?.field_types || [];
  const { mutate: createRule } = useCreateRuleMutation();
  const { mutate: updateRule } = useEditRuleMutation();
  const { mutate: deleteRule } = useDeleteRulesMutation();
  const { data: rule, isLoading: isRuleLoading, isError: hasRuleError } = useGetRuleQuery(ruleId);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm({ defaultValues: DEFAULT_FORM_VALUES });

  const updatedTypes = useMemo(() => {
    return fieldTypes.map(type => ({ value: type, label: type }));
  }, [fieldTypes]);

  useEffect(() => {
    if (rule && !isRuleLoading && !hasRuleError) {
      reset({
        name: rule.name || '',
        ruleISsmallplaintextbox: rule.ruleISsmallplaintextbox || '',
        type: updatedTypes.find(t => t.value === rule.type) || { value: 'text', label: 'text' }
      });
    }
  }, [rule, isRuleLoading, hasRuleError, reset, updatedTypes]);

  const ruleNameWatch = watch('name');
  const typeWatch = watch('type');

  const cleanUp = () => {
    reset(DEFAULT_FORM_VALUES);
    resetRuleId && resetRuleId();
    onClose();
  };

  const onSubmit = data => {
    if (Array.isArray(existedRules)) {
      const ruleExists = existedRules.some(rule => rule.name.toLowerCase() === data.name.toLowerCase());
      if (ruleExists && !ruleId) {
        return Notiflix.Notify.warning(`${data.name} already exists`);
      }
    } else {
      console.warn('existedRules is not an array:', existedRules);
    }

    const values = {
      ...data,
      type: data.type.value,
      userISbb_usersID: user.userID
    };
    if (ruleId) {
      values['_id'] = ruleId;
      updateRule(
        { id: ruleId, values },
        {
          onSuccess: () => {
            cleanUp();
          }
        }
      );
    } else {
      createRule(values, {
        onSuccess: () => {
          cleanUp();
        }
      });
    }
  };

  const handleDelete = () => {
    let confirmAction = confirm('Are you sure?');
    if (confirmAction) {
      deleteRule([ruleId], {
        onSuccess: () => {
          cleanUp();
        }
      });
    }
  };

  if (hasRuleError) {
    return <h2>Can't Load Rule</h2>;
  }

  return (
    <Modal size="lg" show={isVisible} onHide={cleanUp} aria-labelledby="manage-rules-modal">
      <Modal.Header>
        <Modal.Title id="manage-rules-modal">Rule</Modal.Title>
        <FalconCloseButton onClick={cleanUp} />
      </Modal.Header>
      <Modal.Body>
        {isRuleLoading ? (
          <Loader />
        ) : (
          <Form className="mt-2" onSubmit={handleSubmit(onSubmit)}>
            <FormGroup className="mb-2">
              <Row className="g-2">
                <Col md>
                  <Form.Label>Rule Name</Form.Label>
                  <Form.Control
                    placeholder="Enter a name"
                    isInvalid={!!errors.name}
                    {...register('name', { required: 'Name is required' })}
                    type="text"
                  />
                  {errors.name && <Form.Control.Feedback type="invalid">{errors.name.message}</Form.Control.Feedback>}
                </Col>
                <Col md>
                  <Form.Label>Validation</Form.Label>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: 'Type is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={typeWatch || updatedTypes[0]}
                        options={updatedTypes}
                        className="react-select poppins-regular"
                        placeholder="Select type"
                        isDisabled={!ruleNameWatch}
                        key="validation"
                        isSearchable={false}
                      />
                    )}
                  />
                  {errors.type && <Form.Control.Feedback type="invalid">{errors.type.message}</Form.Control.Feedback>}
                </Col>
              </Row>
            </FormGroup>
            <FormGroup className="mb-3">
              <Form.Label>Rule Description</Form.Label>
              <Form.Control
                as="textarea"
                rows="9"
                disabled={!ruleNameWatch}
                placeholder="Enter a description"
                {...register('ruleISsmallplaintextbox', { required: 'Description is required' })}
                type="text"
                isInvalid={!!errors.ruleISsmallplaintextbox}
              />
              {errors.ruleISsmallplaintextbox && (
                <Form.Control.Feedback type="invalid">{errors.ruleISsmallplaintextbox.message}</Form.Control.Feedback>
              )}
            </FormGroup>
            {ruleId ? (
              <Form.Group className="mb-3 text-end">
                <Button variant="outline-secondary" className="w-25" type="submit">
                  Save
                </Button>
                <Button variant="falcon-danger" className="w-25 ms-3" type="button" onClick={handleDelete}>
                  Delete
                </Button>
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Button className="w-100 bg-dark" type="submit" disabled={!ruleNameWatch}>
                  Create
                </Button>
              </Form.Group>
            )}
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

ManageRulesModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ruleId: PropTypes.string,
  resetRuleId: PropTypes.func
};

export default ManageRulesModal;
